import type { CSSProperties, RefObject } from "react";
import { useLayoutEffect, useState } from "react";

export type MenuPositionOptions = {
  offset?: number;
  align?: "left" | "right";
  minWidth?: number;
};

export const useMenuPosition = (
  anchorRef: RefObject<HTMLElement | null>,
  open: boolean,
  options: MenuPositionOptions = {}
) => {
  const [style, setStyle] = useState<CSSProperties>({});
  const { offset = 8, align = "right", minWidth } = options;

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;

    const updatePosition = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      const baseStyle: CSSProperties = {
        position: "fixed",
        top: rect.bottom + offset,
        zIndex: 50
      };

      if (align === "right") {
        baseStyle.right = Math.max(12, window.innerWidth - rect.right);
      } else {
        baseStyle.left = Math.max(12, rect.left);
      }

      if (minWidth) {
        baseStyle.minWidth = minWidth;
      }

      setStyle(baseStyle);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRef, open, offset, align, minWidth]);

  return style;
};
